import { ref } from 'vue'

const PREFERRED_CAMERA_LABEL_STORAGE_KEY = 'preferred_camera_label'

// Lightweight tracing to understand which API calls may trigger permission prompts
// Включается только если переменная окружения VITE_DEBUG_UI строго равна 'true'
const PROMPT_TRACE =
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_DEBUG_UI ===
    'true'
type TraceKind = 'gum' | 'enum' | 'apply' | 'probe' | 'sc'
const traceCounters: Record<TraceKind, number> = { gum: 0, enum: 0, apply: 0, probe: 0, sc: 0 }
const MAX_ALERTS_PER_KIND = 5
const traceAlert = (kind: TraceKind, title: string, details?: string) => {
    if (!PROMPT_TRACE) return
    try {
        if (traceCounters[kind] >= MAX_ALERTS_PER_KIND) return
        traceCounters[kind] += 1
        const msg = `[CameraTrace] ${kind}${title}${details ? `\n${details}` : ''}`
        alert(msg)
    } catch {}
}

// Глобальное состояние камеры (сохраняется между размонтированиями компонентов)
// Это позволяет переиспользовать камеру при навигации между страницами
const globalIsActive = ref(false)
const globalStream = ref<MediaStream | null>(null)
const globalError = ref<string | null>(null)
const globalPreferredDeviceId = ref<string | null>(null)
// Reserved for potential runtime usage
let preferredCameraLoaded = false

type FocusCapableConstraints = MediaTrackConstraints & {
    focusMode?: string | { ideal: string }
}
type FocusCapableMediaStreamConstraints = MediaStreamConstraints & {
    video?: boolean | FocusCapableConstraints
}
type GetDevicesOptions = { revealLabels?: boolean }

// Глобально отключаем использование localStorage для камеры и разрешений (не используется)

const loadPreferredCameraId = () => {
    if (preferredCameraLoaded) return

    preferredCameraLoaded = true

    // Хранение preferred camera id в localStorage отключено
    globalPreferredDeviceId.value = null
}

// Хранение предпочтительного лейбла камеры помогает выбрать ту же камеру в следующей сессии
const getStoredPreferredCameraLabel = (): string | null => {
    try {
        const val = window.localStorage?.getItem(PREFERRED_CAMERA_LABEL_STORAGE_KEY) || null
        return val
    } catch {
        return null
    }
}
const setStoredPreferredCameraLabel = (label: string | null) => {
    try {
        if (label) window.localStorage?.setItem(PREFERRED_CAMERA_LABEL_STORAGE_KEY, label)
        else window.localStorage?.removeItem(PREFERRED_CAMERA_LABEL_STORAGE_KEY)
    } catch {}
}

// Persist preferred camera id is intentionally disabled (no-op)

// Deduplicate concurrent enumerateDevices calls
let enumerateDevicesInFlight: Promise<MediaDeviceInfo[]> | null = null

const enumerateAllDevices = async (): Promise<MediaDeviceInfo[]> => {
    if (!navigator.mediaDevices?.enumerateDevices) return []
    if (!enumerateDevicesInFlight) {
        enumerateDevicesInFlight = navigator.mediaDevices.enumerateDevices().finally(() => {
            enumerateDevicesInFlight = null
        })
    }
    try {
        const list = await enumerateDevicesInFlight
        return list
    } catch {
        return []
    }
}

// Call getUserMedia inside a microtask to improve stability (Safari/iOS quirks)
const getUserMediaDelayed = (params: MediaStreamConstraints): Promise<MediaStream> => {
    return new Promise((resolve, reject) => {
        setTimeout(async () => {
            // Pre-gUM enumerate to log what we know before prompting
            try {
                const preList = await enumerateAllDevices()
                const preInfo = preList
                    .map((d, i) => {
                        const short = (s: string) =>
                            s ? s.slice(0, 8) + (s.length > 8 ? '…' : '') : ''
                        const label = d.label || '<empty>'
                        return `${i + 1}. kind=${d.kind}, label="${label}", id=${short(
                            d.deviceId
                        )}, group=${short(d.groupId)}`
                    })
                    .join('\n')
                traceAlert('enum', 'enumerateDevices(pre-gUM): result', preInfo)
            } catch {}
            // Also log supported constraint names (does not trigger prompt)
            try {
                const supported = navigator.mediaDevices.getSupportedConstraints()
                traceAlert('sc', 'getSupportedConstraints(): result', JSON.stringify(supported))
            } catch {}
            const videoInfo = (() => {
                type AnyRecord = Record<string, unknown>
                const v = (params && (params as unknown as AnyRecord).video) || {}
                const pick = (o: AnyRecord, k: string) =>
                    o && o[k] !== undefined ? JSON.stringify(o[k]) : '—'
                return `video: width=${pick(v as AnyRecord, 'width')}, height=${pick(
                    v as AnyRecord,
                    'height'
                )}, facingMode=${pick(v as AnyRecord, 'facingMode')}, deviceId=${pick(
                    v as AnyRecord,
                    'deviceId'
                )}`
            })()
            traceAlert('gum', 'getUserMedia(): about to request', videoInfo)
            navigator.mediaDevices
                .getUserMedia(params)
                .then((stream) => {
                    try {
                        const t = stream.getVideoTracks()[0]
                        const s = t?.getSettings()
                        const label = t?.label || 'n/a'
                        // Track capabilities (does not trigger prompt; available after gUM)
                        try {
                            const caps = t?.getCapabilities?.()
                            traceAlert(
                                'apply',
                                'track.getCapabilities(): result',
                                JSON.stringify(caps)
                            )
                        } catch {}
                        traceAlert(
                            'gum',
                            'getUserMedia(): success',
                            `track.label=${label}, settings=${JSON.stringify({
                                width: s?.width,
                                height: s?.height,
                                frameRate: s?.frameRate,
                                facingMode: s?.facingMode,
                            })}`
                        )
                    } catch {}
                    resolve(stream)
                })
                .catch((err) => {
                    try {
                        const name = (err as { name?: string })?.name || String(err)
                        traceAlert('gum', 'getUserMedia(): error', `${name}`)
                    } catch {}
                    reject(err)
                })
        }, 0)
    })
}

const setStoredPreferredCameraId = (deviceId: string | null) => {
    loadPreferredCameraId()
    globalPreferredDeviceId.value = deviceId
}

const clearStoredPreferredCameraId = () => {
    setStoredPreferredCameraId(null)
}

const sanitizeForGetUserMedia = (
    constraints: MediaStreamConstraints,
    preferredDeviceId: string | null
): MediaStreamConstraints => {
    const normalized: FocusCapableMediaStreamConstraints = {
        audio: false,
        ...constraints,
    }

    const currentVideo = normalized.video

    if (typeof currentVideo === 'boolean' || currentVideo === undefined) {
        const baseConstraints: FocusCapableConstraints = preferredDeviceId
            ? {
                  deviceId: { exact: preferredDeviceId },
              }
            : {
                  facingMode: { ideal: 'environment' },
              }

        normalized.video = baseConstraints
        return normalized
    }

    const videoConstraints: FocusCapableConstraints = {
        ...(currentVideo as MediaTrackConstraints),
    }

    if (preferredDeviceId) {
        videoConstraints.deviceId = { exact: preferredDeviceId }
        if ('facingMode' in videoConstraints) {
            delete videoConstraints.facingMode
        }
    } else if (!videoConstraints.facingMode) {
        videoConstraints.facingMode = { ideal: 'environment' }
    }

    normalized.video = videoConstraints

    return normalized
}

const sanitizeForApplyConstraints = (
    video: MediaStreamConstraints['video']
): MediaTrackConstraints | null => {
    if (!video || typeof video === 'boolean') {
        return null
    }

    const sanitized: FocusCapableConstraints = { ...(video as MediaTrackConstraints) }

    if ('deviceId' in sanitized) {
        delete sanitized.deviceId
    }

    if ('facingMode' in sanitized) {
        delete sanitized.facingMode
    }
    if ('focusMode' in sanitized) {
        delete sanitized.focusMode
    }

    return sanitized
}
export function useCamera() {
    // Используем глобальные ref вместо локальных
    const isActive = globalIsActive
    const stream = globalStream
    const error = globalError

    /**
     * Запуск камеры с улучшенной retry логикой
     * Поддерживает несколько уровней constraints и exponential backoff
     */
    const startCameraWithRetry = async (
        constraintsLevels: MediaStreamConstraints[],
        signal?: AbortSignal
    ): Promise<MediaStream> => {
        if (!constraintsLevels.length) {
            throw new Error('No constraints provided for camera start')
        }

        loadPreferredCameraId()

        // We try starting from a reasonable quality (index 1 if exists), then relax.
        const initialIndex = Math.min(1, constraintsLevels.length - 1)
        const attemptIndices: number[] = [
            initialIndex,
            ...Array.from(
                { length: constraintsLevels.length - (initialIndex + 1) },
                (_, i) => initialIndex + 1 + i
            ),
        ]

        let preferredDeviceId: string | null = globalPreferredDeviceId.value
        let lastError: unknown = null

        for (const index of attemptIndices) {
            if (signal?.aborted) {
                throw new DOMException('Camera start aborted', 'AbortError')
            }

            const idOptions = preferredDeviceId ? [preferredDeviceId, null] : [null]

            for (const idOption of idOptions) {
                if (signal?.aborted) {
                    throw new DOMException('Camera start aborted', 'AbortError')
                }

                const attemptConstraints = sanitizeForGetUserMedia(
                    constraintsLevels[index],
                    idOption
                )

                // Trace this attempt (to correlate with permission prompts)
                try {
                    const v = (attemptConstraints as FocusCapableMediaStreamConstraints)
                        .video as Record<string, unknown>
                    let deviceIdType = 'none'
                    if (v && typeof v === 'object' && 'deviceId' in v) {
                        const di = (v as Record<string, unknown>).deviceId as
                            | { exact?: unknown; ideal?: unknown }
                            | unknown
                        if (di && typeof di === 'object') {
                            if ('exact' in (di as Record<string, unknown>)) deviceIdType = 'exact'
                            else if ('ideal' in (di as Record<string, unknown>))
                                deviceIdType = 'ideal'
                        }
                    }
                    traceAlert(
                        'gum',
                        'startCameraWithRetry: attempt',
                        JSON.stringify({
                            levelIndex: index,
                            idOption: idOption || null,
                            deviceIdType,
                        })
                    )
                } catch {}

                try {
                    // Stop existing stream BEFORE requesting new one
                    // This forces browser to create a fresh stream with the specified deviceId
                    if (stream.value) {
                        stream.value.getTracks().forEach((track) => track.stop())
                    }

                    const newStream = await getUserMediaDelayed(attemptConstraints)

                    stream.value = newStream
                    isActive.value = true
                    error.value = null

                    const videoTrack = newStream.getVideoTracks()[0]
                    if (videoTrack) {
                        // Try to upgrade constraints to higher level(s) where possible via applyConstraints
                        const upgradeCandidates = constraintsLevels
                            .map((level, i) => ({ level, index: i }))
                            .filter(({ index: i }) => i < initialIndex)

                        for (const { level } of upgradeCandidates) {
                            const sanitized = sanitizeForApplyConstraints(level.video)
                            if (!sanitized) continue
                            try {
                                await videoTrack.applyConstraints(sanitized)
                                break
                            } catch {
                                // Failed to upgrade constraints, continue with current settings
                            }
                        }
                    }

                    return newStream
                } catch (err) {
                    lastError = err
                    const currentError = err as Error & {
                        constraint?: string
                        constraintName?: string
                    }

                    if (currentError.name === 'NotAllowedError') {
                        error.value = getErrorMessage(currentError)
                        isActive.value = false
                        throw currentError
                    }

                    // If the failure is due to deviceId being invalid/stale — clear it and retry without it
                    const ocName = currentError.constraint || currentError.constraintName
                    if (
                        currentError.name === 'OverconstrainedError' &&
                        ocName === 'deviceId' &&
                        idOption
                    ) {
                        clearStoredPreferredCameraId()
                        preferredDeviceId = null
                        // Continue to next attempt (which will try without deviceId)
                        continue
                    }

                    // Otherwise, proceed to the next, more permissive constraints level
                }
            }
        }

        // Final minimal fallback: try minimal constraints but prefer back camera
        try {
            if (signal?.aborted) {
                throw new DOMException('Camera start aborted', 'AbortError')
            }

            const minimal: MediaStreamConstraints = {
                video: { facingMode: { ideal: 'environment' } },
                audio: false,
            }
            const newStream = await getUserMediaDelayed(minimal)

            if (stream.value) {
                stream.value.getTracks().forEach((track) => track.stop())
            }

            stream.value = newStream
            isActive.value = true
            error.value = null
            return newStream
        } catch (e) {
            const finalError =
                lastError instanceof Error
                    ? lastError
                    : e instanceof Error
                      ? e
                      : new Error('Camera start failed')
            error.value = getErrorMessage(finalError)
            isActive.value = false
            throw finalError
        }
    }

    /**
     * Остановка камеры
     */
    const stopCamera = () => {
        if (stream.value) {
            stream.value.getTracks().forEach((track) => {
                track.stop()
            })
            stream.value = null
        }
        isActive.value = false
        error.value = null
    }

    /**
     * Получить список доступных камер
     */
    const getCameraDevices = async (options?: GetDevicesOptions): Promise<MediaDeviceInfo[]> => {
        try {
            let devices = await enumerateAllDevices()
            const videoInputs = devices.filter((device) => device.kind === 'videoinput')

            const allLabelsEmpty = videoInputs.length > 0 && videoInputs.every((d) => !d.label)
            // Избегаем дополнительного gUM для «раскрытия» label, чтобы не вызывать лишний промпт
            if (allLabelsEmpty && options?.revealLabels !== false) {
                try {
                    traceAlert(
                        'gum',
                        'getUserMedia(label reveal): about to request',
                        '{ video: { facingMode: { ideal: \"environment\" } }, audio: false }'
                    )
                    const tmp = await getUserMediaDelayed({
                        video: { facingMode: { ideal: 'environment' } },
                        audio: false,
                    })
                    tmp.getVideoTracks().forEach((t) => t.stop())
                    devices = await enumerateAllDevices()
                } catch {}
            }
            return devices.filter((device) => device.kind === 'videoinput')
        } catch (err) {
            console.error('Failed to get camera devices:', err)
            return []
        }
    }

    /**
     * Получить человекочитаемое сообщение об ошибке
     */
    const getErrorMessage = (error: unknown): string => {
        if (error instanceof Error) {
            switch (error.name) {
                case 'NotAllowedError':
                    return 'Camera access denied. Please allow camera access in your browser settings.'
                case 'NotFoundError':
                    return 'Camera not found. Make sure the camera is connected and not in use by another app.'
                case 'NotReadableError':
                    return 'The camera is unavailable. It may be in use by another app.'
                case 'OverconstrainedError':
                    return 'The camera does not support the required parameters.'
                case 'SecurityError':
                    return 'Security error. Make sure the site is using HTTPS.'
                case 'TypeError':
                    return 'Type error. Check camera support in your browser.'
                case 'AbortError':
                    return 'Camera launch cancelled.'
                default:
                    return error.message || 'Unknown camera error'
            }
        }
        return 'Unknown camera error'
    }

    // Примечание: НЕ останавливаем камеру при размонтировании,
    // чтобы избежать повторных запросов разрешения при навигации между страницами.
    // Браузер автоматически остановит камеру когда пользователь закроет приложение.
    // Для явной остановки используйте метод stopCamera() в компонентах по необходимости.

    return {
        // State
        isActive,
        stream,
        error,

        // Methods
        startCameraWithRetry,
        stopCamera,
        getCameraDevices,
        // Label-based preference helpers
        getStoredPreferredCameraLabel,
        setStoredPreferredCameraLabel,
    }
}
